import { createSelector } from 'reselect';
import { unionBy } from 'lodash';
import { DriverDto, ProductLine } from '@trucktrax/trucktrax-ts-common';
import { ReduxState } from '..';
import { ConversationToMessagesDto } from '../../components/shared/messages';
import { selectedPlantUrlsSelector } from './plantSelectors';
import { buildTruckMap, includeExtraDriverDetails, DriverDetail } from './messageSelectors';

// Input selectors - extract raw state
const getAllDrivers = (state: Partial<ReduxState>): DriverDto[] => state.allDrivers ?? [];
const getSelectedProductLine = (state: Partial<ReduxState>): ProductLine | undefined =>
  state.selectedProductLine;
const getAllTrucks = (state: Partial<ReduxState>) => state.allTrucks ?? [];
const getAllMessages = (state: Partial<ReduxState>): ConversationToMessagesDto[] =>
  state.messageList?.allMessages ?? [];
const getAllSentMessages = (state: Partial<ReduxState>): ConversationToMessagesDto[] =>
  state.messageList?.allSentMessages ?? [];
const getTicketsList = (state: Partial<ReduxState>) => state.ticketsList ?? [];

// Memoized selector: Drivers matching product line and selected plants
export const driversMatchingPlantSelector = createSelector(
  [getAllDrivers, getSelectedProductLine, selectedPlantUrlsSelector],
  (
    allDrivers: DriverDto[],
    selectedProductLine: ProductLine | undefined,
    selectedPlantUrls: string[],
  ): DriverDto[] => {
    if (!selectedProductLine) {
      return [];
    }

    const driversMatchingProductLine = allDrivers.filter(
      item =>
        (item.primaryProductLine === selectedProductLine ||
          item?.secondaryProductLine === selectedProductLine) &&
        !item.isExternal,
    );

    const driversMatchingPlant = driversMatchingProductLine.filter(driver =>
      selectedPlantUrls.includes((driver as DriverDto).primaryPlant?.url ?? ''),
    );

    return driversMatchingPlant;
  },
);

// Memoized selector: Drivers with active tickets matching selected plants
export const driversWithActiveTicketsMatchingSelectedPlantsSelector = createSelector(
  [getTicketsList, getAllDrivers, selectedPlantUrlsSelector],
  (tickets, allDrivers: DriverDto[], selectedPlantUrls: string[]): DriverDto[] => {
    const ticketsMatchingPlants = tickets
      .filter(ticket => selectedPlantUrls.includes(ticket.plant?.url ?? ''))
      .filter(Boolean);

    const driversOnTickets = allDrivers.filter(driver =>
      ticketsMatchingPlants.some(ticket => ticket.driver?.url === driver.url),
    );

    return driversOnTickets;
  },
);

// Memoized selector: Combined drivers (matching plants + drivers on tickets)
export const allMessageableDriversSelector = createSelector(
  [driversMatchingPlantSelector, driversWithActiveTicketsMatchingSelectedPlantsSelector],
  (driversMatchingPlants: DriverDto[], driversOnTickets: DriverDto[]): DriverDto[] =>
    unionBy(driversMatchingPlants, driversOnTickets, d => d.url),
);

// Memoized selector: Truck map for driver details
const truckMapSelector = createSelector([getAllTrucks], trucks => buildTruckMap(trucks));

// Memoized selector: Messageable drivers with details
export const messageableDriversWithDetailsSelector = createSelector(
  [allMessageableDriversSelector, truckMapSelector],
  (allDrivers: DriverDto[], trucksMap: Map<string, string>): DriverDetail[] => {
    const driversWithDetails = includeExtraDriverDetails(allDrivers, trucksMap);
    // Filter to only include drivers that are in the allDrivers list
    return driversWithDetails.filter(driverWithDetail =>
      allDrivers.some(driver => driver.url === driverWithDetail.url),
    );
  },
);

// Helper function for filtering messages
function filterMessagesMatchingPlantSelector(
  messages: ConversationToMessagesDto[],
  drivers: DriverDto[],
  plants: string[],
): ConversationToMessagesDto[] {
  const driverUrls = drivers
    .filter(driver => plants.includes((driver as DriverDto).primaryPlant?.url ?? ''))
    .map(driver => driver.url);
  const matchingMessages = messages.filter(({ conversations }) => {
    const includeMessage = conversations.partiesList.some(member =>
      driverUrls.includes(member.url),
    );
    return includeMessage;
  });
  return matchingMessages;
}

// Memoized selector: All messages matching selected plants
export const allMessagesMatchingPlantSelector = createSelector(
  [getAllMessages, allMessageableDriversSelector, selectedPlantUrlsSelector],
  (
    messages: ConversationToMessagesDto[],
    drivers: DriverDto[],
    selectedPlantUrls: string[],
  ): ConversationToMessagesDto[] =>
    filterMessagesMatchingPlantSelector(messages, drivers, selectedPlantUrls),
);

// Memoized selector: All sent messages matching selected plants
export const allSentMessagesMatchingPlantSelector = createSelector(
  [getAllSentMessages, allMessageableDriversSelector, selectedPlantUrlsSelector],
  (
    messages: ConversationToMessagesDto[],
    drivers: DriverDto[],
    selectedPlantUrls: string[],
  ): ConversationToMessagesDto[] =>
    filterMessagesMatchingPlantSelector(messages, drivers, selectedPlantUrls),
);

// Legacy function exports for backward compatibility
export function filterDriversByProductLineAndPlants(
  drivers: DriverDto[],
  selectedPlantUrls: string[],
  selectedProductLine: ProductLine,
) {
  const driversMatchingProductLine =
    drivers.filter(item => {
      const matchesProductLine =
        item.primaryProductLine === selectedProductLine ||
        item?.secondaryProductLine === selectedProductLine;
      return !item.isExternal && matchesProductLine;
    }) ?? [];

  const driversMatchingPlant = driversMatchingProductLine.filter(driver =>
    selectedPlantUrls.includes((driver as DriverDto).primaryPlant?.url ?? ''),
  );

  return driversMatchingPlant;
}
